import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/slices/authSlice";
import { motion, AnimatePresence } from "framer-motion";
import { Menu } from "lucide-react";

const Header = ({ theme, toggleTheme }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    setShowUserMenu(false);
    navigate("/");
  };

  return (
    <header
      style={{
        background: "linear-gradient(135deg, #0f0f0f 0%, #1a0f00 100%)",
        borderBottom: "2px solid #FF7618",
        position: "sticky",
        top: 0,
        zIndex: 50,
        boxShadow: "0 2px 20px rgba(249,115,22,0.3)",
      }}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div
              style={{
                width: "90px",
                height: "60px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src="/images/mech_now.webp"
                alt="mechnow Logo"
                width={600}
                height={400}
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/machines"
              style={{
                fontWeight: 500,
              }}
              className="inline-block
                        bg-[linear-gradient(to_right,#fff_50%,#FF7618_50%)]
                        bg-[length:200%_100%]
                        bg-right
                        bg-clip-text
                        text-transparent
                        transition-all
                        duration-500
                        hover:bg-left"
            >
              Browse Machines
            </Link>

            <Link
              to={"/about"}
              className="inline-block
                        bg-[linear-gradient(to_right,#fff_50%,#FF7618_50%)]
                        bg-[length:200%_100%]
                        bg-right
                        bg-clip-text
                        text-transparent
                        transition-all
                        duration-500
                        hover:bg-left"
            >
              About Us
            </Link>

            {isAuthenticated ? (
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #FF7618, #fbbf24)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "16px",
                      color: "#000",
                    }}
                  >
                    {user.avatar ? (
                      <img
                        src={
                          user.isGoogleUser
                            ? user.avatar
                            : `${process.env.REACT_APP_API_URL}/${user.avatar}`
                        }
                        alt={user?.name?.charAt(0).toUpperCase()}
                        className="rounded-full"
                      />
                    ) : (
                      <span>{user?.name?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <span
                    style={{
                      color: "#fbbf24",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    {user?.name?.split(" ")[0]}
                  </span>
                  <span style={{ color: "#888" }}>▾</span>
                </button>

                {showUserMenu && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "48px",
                      background: "#1a1a1a",
                      border: "1px solid #FF7618",
                      borderRadius: "12px",
                      padding: "8px 0",
                      minWidth: "180px",
                      boxShadow: "0 8px 32px rgba(249,115,22,0.2)",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 16px",
                        borderBottom: "1px solid #333",
                      }}
                    >
                      <div
                        style={{
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: "14px",
                        }}
                      >
                        {user?.name}
                      </div>
                      <div
                        style={{
                          color: "#FF7618",
                          fontSize: "12px",
                          textTransform: "capitalize",
                        }}
                      >
                        {user?.role}
                      </div>
                    </div>
                    {user?.role === "admin" && (
                      <Link
                        to="/admin/dashboard"
                        style={{
                          display: "block",
                          padding: "10px 16px",
                          color: "#fbbf24",
                          fontSize: "14px",
                          textDecoration: "none",
                        }}
                        onClick={() => setShowUserMenu(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    {user?.role === "vendor" && (
                      <Link
                        to="/vendor/dashboard"
                        style={{
                          display: "block",
                          padding: "10px 16px",
                          color: "#fbbf24",
                          fontSize: "14px",
                          textDecoration: "none",
                        }}
                        onClick={() => setShowUserMenu(false)}
                      >
                        Vendor Dashboard
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      style={{
                        display: "block",
                        padding: "10px 16px",
                        color: "#fbbf24",
                        fontSize: "14px",
                        textDecoration: "none",
                      }}
                      onClick={() => setShowUserMenu(false)}
                    >
                      Profile Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 16px",
                        color: "#ef4444",
                        fontSize: "14px",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3 gap-3">
                <Link
                  to="/login"
                  className="inline-block
                        bg-[linear-gradient(to_right,#fff_50%,#FF7618_50%)]
                        bg-[length:200%_100%]
                        bg-right
                        bg-clip-text
                        text-transparent
                        transition-all
                        duration-500
                        hover:bg-left"
                >
                  Sign In
                </Link>
                <Link to="/vendor/register" className="btn-secondary">
                  <span>Become a Vendor</span>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-4 md:hidden">
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fbbf24",
                cursor: "pointer",
                fontSize: "24px",
              }}
            >
              {showMobileMenu ? "✕" : <Menu size={20} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showMobileMenu && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={() => setShowMobileMenu(false)}
                className="fixed inset-0 z-[90] bg-black/10 backdrop-blur-md md:hidden"
              />

              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
                className="fixed top-0 right-0 z-[100] h-screen w-full max-w-full overflow-y-auto bg-transparent shadow-[-10px_0_40px_rgba(249,115,22,0.15)] backdrop-blur-xl md:hidden"
              >
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
                  <img
                    src="/images/mech_now.webp"
                    alt="mechnow Logo"
                    className="h-20 w-auto object-contain"
                  />

                  <button
                    onClick={() => {
                      setShowMobileMenu(false);
                      console.log("Mobile menu closed");
                    }}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-orange-500/30 text-xl text-orange-400 transition hover:bg-orange-500/10"
                  >
                    ✕
                  </button>
                </div>

                <div className="flex flex-col px-6 py-6">
                  {[
                    { to: "/machines", label: "Browse Machines" },
                    { to: "/about", label: "About Us" },
                  ].map((link, index) => (
                    <motion.div
                      key={link.to}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: index * 0.06,
                        duration: 0.25,
                      }}
                    >
                      <Link
                        to={link.to}
                        onClick={() => setShowMobileMenu(false)}
                        className="block border-b border-white/10 py-4 text-base font-medium text-gray-200 transition hover:translate-x-1 hover:text-orange-400"
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}

                  <div className="mt-6">
                    {isAuthenticated ? (
                      <button
                        onClick={handleLogout}
                        className="w-full rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-left font-medium text-red-400 transition hover:bg-red-500/10"
                      >
                        Sign Out
                      </button>
                    ) : (
                      <Link
                        to="/login"
                        onClick={() => setShowMobileMenu(false)}
                        className="block w-full rounded-lg border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-center font-medium text-orange-400 transition hover:bg-orange-500/20"
                      >
                        Sign In
                      </Link>
                    )}
                  </div>

                  {!isAuthenticated && (
                    <Link
                      to="/vendor/register"
                      onClick={() => setShowMobileMenu(false)}
                      className="mt-3 block w-full rounded-lg bg-orange-500 px-4 py-3 text-center font-semibold text-black transition hover:bg-orange-400"
                    >
                      Become a Vendor
                    </Link>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
